/**
 * Fiat Currency Selector Modal
 * Searchable list of fiat currencies with flag icons.
 * Uses API currencies if provided, falls back to static FIAT_CURRENCIES data.
 */

import { useState, useMemo } from 'react';
import { CloseButton } from '../ui/CloseButton';
import { SearchInput } from '../ui/SearchInput';
import { NRLogo } from '../ui/NRLogo';
import { FiatFlag } from '../ui/icons';
import { FIAT_CURRENCIES, type FiatCurrency } from '../../data/cryptoData';
import { useT } from '../../i18n';

const POPULAR_FIATS = ['USD', 'EUR', 'GBP'];

export interface FiatCurrencyModalProps {
	onClose: () => void;
	onSelect: (code: string) => void;
	/** Currencies from API config — takes precedence over static fallback */
	currencies?: Array<{ code: string; name?: string }>;
	/** Base URL for static assets (fiat flags) */
	assetBaseUrl?: string;
	/** Currently selected currency code */
	selectedCode?: string;
}

export function FiatCurrencyModal({
	onClose,
	onSelect,
	currencies,
	assetBaseUrl = '',
	selectedCode,
}: FiatCurrencyModalProps) {
	const t = useT();
	const [search, setSearch] = useState('');

	// Merge API data with static fallback for display names
	const currencyList = useMemo<FiatCurrency[]>(() => {
		if (!currencies || currencies.length === 0) return FIAT_CURRENCIES;

		// Build a lookup from static data for display names
		const staticMap = new Map(FIAT_CURRENCIES.map((c) => [c.code, c.name]));

		return currencies.map((c) => ({
			code: c.code,
			name: c.name || staticMap.get(c.code) || c.code,
		}));
	}, [currencies]);

	const filtered = useMemo(() => {
		const raw = search.trim().toLowerCase();

		// If no search, put selected item first
		if (!raw) {
			if (selectedCode) {
				const selected = currencyList.find((c) => c.code === selectedCode);
				if (selected) {
					const rest = currencyList.filter((c) => c.code !== selectedCode);
					return [selected, ...rest];
				}
			}
			return currencyList;
		}

		return currencyList.filter(
			(c) => c.code.toLowerCase().includes(raw) || c.name.toLowerCase().includes(raw)
		);
	}, [currencyList, search, selectedCode]);

	return (
		<>
			{/* Modal header — 128px with blurred bg */}
			<div className="relative shrink-0" style={{ height: 128, background: 'var(--t-header-bg)', backdropFilter: 'blur(var(--t-header-blur))', WebkitBackdropFilter: 'blur(var(--t-header-blur))' }}>
				<div className="absolute" style={{ left: 12, top: 20 }}>
					<NRLogo width={46} />
				</div>
				<p className="absolute text-[16px] text-center truncate" style={{ top: 28, left: 64, right: 64, color: 'var(--t-text)', lineHeight: '24px' }}>
					{t('modal.selectFiat')}
				</p>
				<div className="absolute" style={{ right: 12, top: 20 }}>
					<CloseButton onClick={onClose} />
				</div>
				<div className="absolute" style={{ bottom: 8, left: 12, right: 12 }}>
					<SearchInput value={search} onChange={setSearch} inline />
				</div>
			</div>
			<div className="flex-1 overflow-y-auto custom-scroll px-3">
				{filtered.map((c, i) => {
					const isSelected = selectedCode === c.code;
					return (
						<button
							key={c.code}
							onClick={() => onSelect(c.code)}
							className="flex items-center w-full card-press hover-item modal-item-in"
							style={{ gap: 16, animationDelay: `${Math.min(i * 45, 350)}ms` }}
						>
							<FiatFlag currencyCode={c.code} size={32} baseUrl={assetBaseUrl} />
							<div className="flex-1 flex items-center" style={{ borderBottom: '1px solid var(--t-card-border)', paddingTop: 20, paddingBottom: 20, gap: 10 }}>
								<span className="flex-1 text-[18px] text-left" style={{ color: 'var(--t-text)', lineHeight: '20px' }}>
									{c.code}
								</span>
								{POPULAR_FIATS.includes(c.code) && (
									<span className="text-[11px] font-medium" style={{ border: '1.5px solid var(--t-card-border)', borderRadius: 100, paddingLeft: 8, paddingRight: 8, color: 'var(--t-text-secondary)', lineHeight: '20px', letterSpacing: '0.22px' }}>
										{t('modal.popular')}
									</span>
								)}
								{isSelected && (
									<div style={{ width: 40, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
											<path
												d="M13.5 4.5L6 12L2.5 8.5"
												stroke="var(--t-success, #4CD964)"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
								)}
							</div>
						</button>
					);
				})}
				{filtered.length === 0 && (
					<div className="text-center py-10 text-[14px]" style={{ color: 'var(--t-text-muted)' }}>
						{t('modal.noCurrencies')}
					</div>
				)}
			</div>
		</>
	);
}
